import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, FileText, Link2, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import type { PageNodeBody } from '@/shared/api/generated/model';
import { cx } from '@/shared/lib/cx';
import { useDragSensors } from '@/shared/lib/dnd';
import { t } from '@/shared/i18n';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/shared/ui/ContextMenu';
import { toast } from '@/shared/ui/Toast';

import {
  buildTree,
  flatten,
  projectDrop,
  subtreeIDs,
  type FlatPage,
  type Projection,
} from './tree';
import styles from './PageTree.module.scss';

interface Props {
  items: readonly PageNodeBody[];
  activeID: string | undefined;
  canEdit: boolean;
  /** Ссылка на страницу — уровень (организация / проект) знает вызывающий. */
  href: (id: string) => string;
  onOpen: (id: string) => void;
  onCreate: (parentID?: string) => void;
  onMove: (id: string, parentID: string | null, afterID: string | null) => void;
  /** Удалить страницу (с подтверждением снаружи); нет — пункта в меню нет. */
  onDelete?: ((id: string) => void) | undefined;
  /** Компактный вид для сайдбара: без пустого состояния с подсказкой. */
  compact?: boolean;
}

/**
 * Дерево страниц: раскрытие веток, «+» подстраницы при наведении и
 * перетаскивание. Тянуть можно и вверх-вниз, и вправо — глубина считается
 * от горизонтального сдвига (pageTree.projectDrop), подсветка показывает,
 * куда ляжет. Ветка переезжает целиком.
 */
export function PageTree({
  items,
  activeID,
  canEdit,
  href,
  onOpen,
  onCreate,
  onMove,
  onDelete,
  compact = false,
}: Props) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [dragging, setDragging] = useState<FlatPage | null>(null);
  const [projection, setProjection] = useState<Projection | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [dx, setDx] = useState(0);

  const tree = useMemo(() => buildTree(items), [items]);

  // Пока тащим ветку — её потомков в списке нет: они едут с ней.
  const flat = useMemo(() => {
    const all = flatten(tree, collapsed);

    if (!dragging) {
      return all;
    }

    const branch = subtreeIDs(tree, dragging.id);

    return all.filter((item) => item.id === dragging.id || !branch.has(item.id));
  }, [tree, collapsed, dragging]);

  const sensors = useDragSensors({ keyboard: false });

  const toggle = (id: string) => {
    setCollapsed((set) => {
      const next = new Set(set);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const reproject = (over: string | undefined, delta: number) => {
    if (!dragging || over === undefined) {
      setProjection(null);

      return;
    }

    const without = flat.filter((item) => item.id !== dragging.id);
    const activeIndex = flat.findIndex((item) => item.id === dragging.id);
    let index: number;

    if (over === dragging.id) {
      // Над собой — остаётся в своей щели, меняется только глубина.
      index = Math.min(activeIndex, without.length);
    } else {
      const overIndex = flat.findIndex((item) => item.id === over);

      // Вниз — встать после строки, на которую опустили; вверх — перед ней.
      index = without.findIndex((item) => item.id === over) + (overIndex > activeIndex ? 1 : 0);
    }

    setOverIndex(index);
    setProjection(projectDrop(without, dragging.depth, index, delta));
  };

  const onDragOver = (event: DragOverEvent) => {
    reproject(event.over?.id as string | undefined, dx);
  };

  const onDragMove = (event: DragMoveEvent) => {
    setDx(event.delta.x);
    reproject(event.over?.id as string | undefined, event.delta.x);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const active = dragging;
    const target = projection;

    setDragging(null);
    setProjection(null);
    setOverIndex(null);
    setDx(0);

    if (!active || !target || event.over === null) {
      return;
    }

    // Ничего не поменялось — не дёргать сервер.
    const current = currentPlace(flat, active);

    if (current.parentID === target.parentID && current.afterID === target.afterID) {
      return;
    }

    onMove(active.id, target.parentID, target.afterID);
  };

  if (items.length === 0) {
    return compact ? null : (
      <div className={styles['empty']}>
        <div>{t.pages.empty}</div>
        <div>{t.pages.emptyHint}</div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(event) => {
        const item = flat.find((entry) => entry.id === event.active.id);

        if (item) {
          setDragging(item);
          setDx(0);
        }
      }}
      onDragOver={onDragOver}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setDragging(null);
        setProjection(null);
        setOverIndex(null);
      }}
    >
      <SortableContext items={flat.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <ul className={styles['tree']}>
          {flat.map((item, index) => (
            <Row
              key={item.id}
              item={item}
              depth={dragging?.id === item.id && projection ? projection.depth : item.depth}
              active={item.id === activeID}
              dragging={dragging?.id === item.id}
              projectedParent={
                projection !== null && overIndex !== null && projection.parentID === item.id
              }
              open={!collapsed.has(item.id)}
              canEdit={canEdit}
              href={href(item.id)}
              onToggle={() => {
                toggle(item.id);
              }}
              onOpen={() => {
                onOpen(item.id);
              }}
              onCreate={() => {
                setCollapsed((set) => {
                  const next = new Set(set);

                  next.delete(item.id);

                  return next;
                });
                onCreate(item.id);
              }}
              onDelete={onDelete === undefined ? undefined : () => onDelete(item.id)}
              index={index}
            />
          ))}
        </ul>
      </SortableContext>

      {createPortal(
        <DragOverlay zIndex={1000} dropAnimation={null}>
          {dragging && (
            <div className={styles['overlay']}>
              <span className={styles['icon']}>
                {dragging.icon === '' ? <FileText /> : dragging.icon}
              </span>
              {dragging.title === '' ? t.pages.untitled : dragging.title}
            </div>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

interface RowProps {
  item: FlatPage;
  depth: number;
  active: boolean;
  dragging: boolean;
  projectedParent: boolean;
  open: boolean;
  canEdit: boolean;
  href: string;
  index: number;
  onToggle: () => void;
  onOpen: () => void;
  onCreate: () => void;
  onDelete: (() => void) | undefined;
}

function Row({
  item,
  depth,
  active,
  dragging,
  projectedParent,
  open,
  canEdit,
  href,
  onToggle,
  onOpen,
  onCreate,
  onDelete,
}: RowProps) {
  // Деструктуризация, а не объект: линтер React Compiler считает обращение
  // к полям результата хука доступом к ref во время рендера.
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item.id,
    disabled: !canEdit,
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${href}`);
      toast.success(t.task.linkCopied);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  return (
    <li
      ref={setNodeRef}
      className={styles['item']}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={cx(
              styles['row'],
              active && styles['row--active'],
              dragging && styles['row--dragging'],
              projectedParent && styles['row--projected'],
            )}
            style={{ '--depth': depth } as React.CSSProperties}
            {...attributes}
            {...listeners}
          >
            <button
              type="button"
              className={cx(
                styles['toggle'],
                open && styles['toggle--open'],
                !item.hasChildren && styles['toggle--empty'],
              )}
              aria-label={open ? t.pages.collapse : t.pages.expand}
              tabIndex={item.hasChildren ? 0 : -1}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}
            >
              <ChevronRight />
            </button>
            <a
              className={styles['link']}
              href={href}
              onClick={(event) => {
                // Своя навигация: роутер без перезагрузки; middle-click и
                // ⌘-клик остаются браузеру.
                if (event.metaKey || event.ctrlKey || event.button !== 0) {
                  return;
                }

                event.preventDefault();
                onOpen();
              }}
            >
              <span className={styles['icon']}>{item.icon === '' ? <FileText /> : item.icon}</span>
              <span className={cx(styles['title'], item.title === '' && styles['title--untitled'])}>
                {item.title === '' ? t.pages.untitled : item.title}
              </span>
            </a>
            {canEdit && (
              <button
                type="button"
                className={styles['row__add']}
                aria-label={t.pages.addChild}
                title={t.pages.addChild}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  onCreate();
                }}
              >
                <Plus />
              </button>
            )}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem icon={<FileText />} onSelect={onOpen}>
            {t.pages.open}
          </ContextMenuItem>
          {canEdit && (
            <ContextMenuItem icon={<Plus />} onSelect={onCreate}>
              {t.pages.addChild}
            </ContextMenuItem>
          )}
          <ContextMenuItem
            icon={<Link2 />}
            onSelect={() => {
              void copyLink();
            }}
          >
            {t.task.copyLink}
          </ContextMenuItem>
          {onDelete !== undefined && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem icon={<Trash2 />} tone="danger" onSelect={onDelete}>
                {t.pages.delete}
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
    </li>
  );
}

/** Где страница стоит сейчас: родитель и сосед перед ней той же глубины. */
function currentPlace(flat: readonly FlatPage[], active: FlatPage): Projection {
  const index = flat.findIndex((item) => item.id === active.id);

  for (let i = index - 1; i >= 0; i -= 1) {
    const item = flat[i];

    if (!item) {
      continue;
    }

    if (item.depth < active.depth) {
      break;
    }

    if (item.depth === active.depth) {
      return { depth: active.depth, parentID: active.parent_id, afterID: item.id };
    }
  }

  return { depth: active.depth, parentID: active.parent_id, afterID: null };
}
